import { Divider, Flex, Steps, Tag, Typography } from "antd";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetBatchById, useGetDeliveriesForBatch } from "../../api/api.get";
import { PillButton } from "../../components/button.component";
import { useNotifications2 } from "../../components/notifications/notification";
import { BATCH_DELIVERY_STATUS } from "../../types/types";
import { capitalize } from "../../utils/functions";
import ConfirmDeliveryModal from "./confirm-delivery.modal";

export default function BatchViewPage() {
  const { id } = useParams();

  const { data } = useGetBatchById(id);

  const { data: deliveriesData, refetch: refetchDeliveriesData } =
    useGetDeliveriesForBatch(id);

  const currentDeliveryCounterIndex = useMemo(() => {
    if (deliveriesData && data) {
      // find the delivery that is currently in progress.
      // With that, find the index from the batch object's deliveries.
      const inProgressDeliveryItem = deliveriesData.find(
        (d) => d.status === BATCH_DELIVERY_STATUS.DELIVERY_PROGRESS,
      );
      if (inProgressDeliveryItem) {
        return data.batchDeliveries.findIndex(
          (bd) => bd.id === inProgressDeliveryItem.id,
        );
      }
      return 0;
    }
    return 0;
  }, [deliveriesData, data]);

  const deliveriesStatusMap = useMemo(() => {
    const map = new Map<string, string>();

    if (deliveriesData) {
      for (const d of deliveriesData) {
        map.set(d.id, d.status);
      }
    }
    return map;
  }, [deliveriesData]);

  const { notification, notifySuccess, notifyError } = useNotifications2();
  const totalDistanceInTrip = useMemo(() => {
    if (data) {
      return data.batchDeliveries.reduce((acc, value) => {
        return value.distance + acc;
      }, 0);
    }
    return 0;
  }, [data]);

  const items = useMemo(() => {
    if (data && data.batchDeliveries) {
      return data.batchDeliveries.map((delivery) => {
        return {
          title: (
            <strong style={{ color: "var(--exhut-ramar-blue)" }}>
              {capitalize(delivery.customerLastName) +
                ", " +
                capitalize(delivery.customerFirstName)}
            </strong>
          ),
          description: (
            <Flex vertical>
              <span>
                {capitalize(delivery.customerStreet1) +
                  ", " +
                  (delivery.customerStreet2 ?? "") +
                  ", " +
                  capitalize(delivery.customerCity) +
                  ", " +
                  delivery.customerZipcode}
              </span>

              <h3>{Math.round((delivery.distance * 0.62) / 1000)} mi</h3>

              {deliveriesStatusMap.get(delivery.id) ===
                BATCH_DELIVERY_STATUS.DELIVERY_COMPLETE && (
                <Flex>
                  <Tag color="#87d068" style={{ color: "black" }}>
                    Completed
                  </Tag>{" "}
                  on
                </Flex>
              )}
              {deliveriesStatusMap.get(delivery.id) ===
                BATCH_DELIVERY_STATUS.DELIVERY_PROGRESS && (
                <Flex>
                  <PillButton
                    title="Drop Complete"
                    onClick={() => setSelectedDeliveryId(delivery.id)}
                  />
                </Flex>
              )}

              <Divider />
            </Flex>
          ),
        };
      });
    }
    return [];
  }, [data, deliveriesStatusMap]);

  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string>();

  return (
    <div className="body p-20">
      {notification}
      <ConfirmDeliveryModal
        open={!!selectedDeliveryId}
        deliveryId={selectedDeliveryId}
        onCancel={() => setSelectedDeliveryId(undefined)}
        onError={() => notifyError("Whoops! Please try again.")}
        onSuccess={() => {
          notifySuccess({
            title: "Success",
            description: "Delivery is complete.",
          });
          refetchDeliveriesData();
          setSelectedDeliveryId(undefined);
        }}
      />
      <Typography.Title>Drop In Progress..</Typography.Title>

      <div className="divCenterAlign ">
        <Flex className="divAlignItemsOnly" gap={10} vertical>
          <strong> Total Distance In Trip: </strong>
          <Typography.Title level={2}>
            {Math.round((totalDistanceInTrip / 1000) * 0.62)} miles
          </Typography.Title>
        </Flex>
      </div>

      <div className="mt-40">
        <Steps
          style={{ marginLeft: "0%" }}
          direction="vertical"
          current={currentDeliveryCounterIndex}
          items={items}
        />
      </div>
    </div>
  );
}
